import { postStorefrontCanvasLoaded } from 'components/storefront/editor/utils/post-message';
import ScriptManager from './script-manager';

/** @typedef {() => Promise<void>} LoaderFn */

const ASSET_LOAD_PHASES = Object.freeze({
  BEFORE_CONTENT: 'layout:before-content',
  CONTENT: 'content',
  AFTER_CONTENT: 'layout:after-content',
  DEFER_SCRIPTS: 'defer-scripts',
});

const ASSET_LOAD_ORDER = Object.freeze([
  ASSET_LOAD_PHASES.BEFORE_CONTENT,
  ASSET_LOAD_PHASES.CONTENT,
  ASSET_LOAD_PHASES.AFTER_CONTENT,
  ASSET_LOAD_PHASES.DEFER_SCRIPTS,
]);

class AssetLoader {
  /** @type {Map<string, Map<number, LoaderFn[]>>} */
  #loaders = new Map();
  #initialSectionPaths = new Set();
  #contentRenderStarted = false;
  #loaded = false;
  #scriptManager = null;

  constructor(initialSectionPaths, theme) {
    this.#initialSectionPaths = initialSectionPaths;
    this.#scriptManager = new ScriptManager(theme);
  }

  get loaded() {
    return this.#loaded;
  }

  /**
   * @param {LoaderFn} loader
   * @param {number} priority
   */
  addLayoutAsset(loader, priority) {
    this.#addAsset(loader, 'layout', priority);
  }

  /**
   * @param {LoaderFn} loader
   * @param {number} priority
   */
  addContentAsset(loader, priority) {
    this.#addAsset(loader, 'content', priority);
  }

  /**
   * @param {LoaderFn} loader
   * @param {'content' | 'layout'} phase
   * @param {number} priority
   * @param {object} params
   */
  addScript(loader, phase, priority, params) {
    // use DEFER_SCRIPTS phase for defer scripts
    const assetPhase =
      params.defer !== undefined ? ASSET_LOAD_PHASES.DEFER_SCRIPTS : null;

    this.#addAsset(loader, phase, priority, assetPhase);
  }

  onContentRenderStart() {
    this.#contentRenderStarted = true;
  }

  onSectionMount(path) {
    this.#initialSectionPaths.delete(path);

    if (this.#areAllSectionsMounted()) {
      this.#load().then(() => {
        if (!this.#loaded) {
          this.#loaded = true;

          // dispatch missed events for scripts
          postStorefrontCanvasLoaded(window.parent);
          const evt = new Event('load');
          window.dispatchEvent(evt);

          const domContentLoadedEvent = new Event('DOMContentLoaded', {
            bubbles: true,
          });
          document.dispatchEvent(domContentLoadedEvent);
        }
      });
    }
  }

  async #load() {
    if (this.#loaders.size <= 0) {
      return;
    }

    for (const phase of ASSET_LOAD_ORDER) {
      const loadPhase = this.#loaders.get(phase);

      if (!loadPhase || loadPhase.size <= 0) {
        continue;
      }

      const priorities = Array.from(loadPhase.keys()).sort();

      for (const priority of priorities) {
        const loaders = loadPhase.get(priority);

        if (!loaders) {
          continue;
        }

        loadPhase.delete(priority);

        await Promise.allSettled(
          loaders.map((loader) =>
            loader({
              scriptManager: this.#scriptManager,
            }),
          ),
        );
      }

      this.#loaders.delete(phase);
    }

    this.#onAllAssetsLoaded();
  }

  #onAllAssetsLoaded() {
    this.#scriptManager.onAllAssetsLoaded();
  }

  /**
   * @param {LoaderFn} loader
   * @param {string} type
   * @param {number} priority
   * @param {string | null} assetPhase - already calculated phase
   */
  #addAsset(loader, type, priority = 0, assetPhase = null) {
    const phase = assetPhase || this.#getAssetLoadPhase(type);

    let loadPhase = this.#loaders.get(phase);

    if (loadPhase === undefined) {
      loadPhase = new Map();
      this.#loaders.set(phase, loadPhase);
    }

    let loaderList = loadPhase.get(priority);

    if (loaderList === undefined) {
      loaderList = [];
      loadPhase.set(priority, loaderList);
    }

    loaderList.push(loader);
  }

  #getAssetLoadPhase(type) {
    if (type !== 'layout') {
      return ASSET_LOAD_PHASES.CONTENT;
    }

    return this.#contentRenderStarted
      ? ASSET_LOAD_PHASES.AFTER_CONTENT
      : ASSET_LOAD_PHASES.BEFORE_CONTENT;
  }

  #areAllSectionsMounted() {
    return this.#initialSectionPaths.size <= 0;
  }
}

export default AssetLoader;
